
import React from 'react';
import { EyeIcon, TargetIcon, GemIcon } from '../components/Icons';
import AnimatedSection from '../components/AnimatedSection';
import PageWrapper from '../components/PageWrapper';

// Reusable card for core principles
const PrincipleCard: React.FC<{ icon: React.ReactNode; title: string; children: React.ReactNode }> = ({ icon, title, children }) => (
    <div className="glow-card h-full p-8 rounded-xl bg-[var(--panel-bg)] backdrop-blur-sm relative border border-[var(--border-color)] text-center">
        <div className="mb-4 w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-gradient-to-br from-[var(--gradient-from)] to-[var(--gradient-to)] text-white">{icon}</div>
        <h3 className="text-2xl font-bold text-[var(--text-primary)] mb-3">{title}</h3>
        <div className="text-[var(--text-secondary)] space-y-2">{children}</div>
    </div>
);

const AboutPage: React.FC = () => {
    return (
        <PageWrapper>
            
            {/* Story Section */}
            <section className="py-20 sm:py-32">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <AnimatedSection className="max-w-4xl mx-auto text-center">
                        <h1 className="text-4xl md:text-6xl font-extrabold text-[var(--text-primary)]">
                           Our Story
                        </h1>
                        <p className="mt-6 text-lg text-[var(--text-secondary)] leading-relaxed">
                           We founded Codemine on a simple but powerful belief: that creating technology should feel as natural as having an idea. Too often, we’ve seen brilliant minds held back — not because their vision wasn’t strong enough, but because the tools to bring that vision to life were locked behind layers of technical barriers, endless complexity, and overwhelming costs.
                        </p>
                         <p className="mt-4 text-lg text-[var(--text-secondary)] leading-relaxed">
                           Codemine exists to change that. We built it to be the bridge between imagination and execution, a platform where your creativity doesn’t just sit on paper, it comes alive instantly. Whether you’re an ambitious founder with a new product idea, a developer tired of repetitive boilerplate, or a creator eager to share your vision with the world — Codemine gives you the power to move forward without hesitation.
                        </p>
                    </AnimatedSection>
                </div>
            </section>

            {/* Mission & Values Section */}
            <section className="py-20 sm:py-24 bg-[var(--panel-bg)]/50">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <AnimatedSection className="text-center mb-16 max-w-3xl mx-auto">
                        <h2 className="text-3xl md:text-4xl font-extrabold text-[var(--text-primary)]">Our Guiding Principles</h2>
                        <p className="mt-4 text-lg text-[var(--text-secondary)]">The philosophy that drives our innovation and commitment to our users.</p>
                    </AnimatedSection>
                    
                    <div className="grid md:grid-cols-1 lg:grid-cols-3 gap-8">
                        <AnimatedSection delay={100}>
                            <PrincipleCard icon={<EyeIcon />} title="Our Vision">
                                <p>To become the world’s most trusted AI-powered builder, enabling anyone, anywhere, to launch their digital dreams effortlessly and instantly.</p>
                            </PrincipleCard>
                        </AnimatedSection>
                        
                        <AnimatedSection delay={200}>
                            <PrincipleCard icon={<TargetIcon />} title="Our Mission">
                                <p>To abstract away the complexities of development, empowering creators to build, iterate, and deploy at the speed of thought.</p>
                            </PrincipleCard>
                        </AnimatedSection>

                        <AnimatedSection delay={300}>
                            <PrincipleCard icon={<GemIcon />} title="Our Values">
                                <ul className="list-none space-y-2">
                                    <li><strong>Innovation:</strong> Relentlessly push boundaries.</li>
                                    <li><strong>Simplicity:</strong> Make technology accessible.</li>
                                    <li><strong>Trust:</strong> Build a reliable platform.</li>
                                </ul>
                            </PrincipleCard>
                        </AnimatedSection>
                    </div>
                </div>
            </section>
            
            {/* Commitment Section */}
            <section className="py-20 sm:py-32">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <AnimatedSection className="text-center max-w-4xl mx-auto">
                        <h2 className="text-3xl md:text-4xl font-extrabold text-[var(--text-primary)]">Our Commitment</h2>
                         <p className="mt-6 text-lg text-[var(--text-secondary)] leading-relaxed">
                          But Codemine isn’t just another tool. It’s your partner in innovation. By harnessing the intelligence of advanced AI, we make it possible to design, build, and deploy apps and websites at the speed of thought. What once took months of planning, coding, and debugging can now happen in minutes — freeing you to focus on what matters most: your story, your customers, your impact.
                        </p>
                        <p className="mt-4 text-lg text-[var(--text-secondary)] leading-relaxed">
                          Our aim is simple: to remove the gap between having an idea and seeing it live in the world. With Codemine, you don’t just create technology — you unlock possibilities, accelerate your journey, and bring your boldest ideas to life faster than ever before.
                        </p>
                    </AnimatedSection>
                </div>
            </section>
        </PageWrapper>
    );
};

export default AboutPage;